/**
 * Ein reines Datum anzeigen — mehr nicht.
 *
 * Dieselbe Zurückhaltung wie `lib/money.ts`: eine deterministische Umrechnung
 * eines vom Server gelieferten Werts in eine Anzeigeform, keine Fachlogik.
 * `due_on` (Spec 04a, „Antwort GET /open-items") und `valid_from`/`valid_to`
 * (`ContractTermSummary`) sind die einzigen Felder dieser Spec, die **ohne**
 * eigenes `*_display` ankommen — anders als jeder Monatsname
 * (`period_display`), den der Server fertig liefert und der Client nie
 * bildet. Tag, Monat und Jahr als Zahl sind keine Wortbildung.
 */

const FORMAT = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })

/**
 * `"2026-07-03"` → `"03.07.2026"`.
 *
 * Zerlegt von Hand statt `new Date(iso)`: Der Konstruktor liest ein reines
 * Datum als UTC-Mitternacht, und in jeder Zeitzone westlich von Greenwich
 * verschiebt die lokale Wiedergabe den Tag um eins zurück.
 */
export function formatDate(iso: string): string {
  const year = Number(iso.slice(0, 4))
  const month = Number(iso.slice(5, 7))
  const day = Number(iso.slice(8, 10))
  return FORMAT.format(new Date(year, month - 1, day))
}

const BERLIN_DAY_FORMAT = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Berlin',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

/**
 * Der heutige Tag in der Zeitzone `Europe/Berlin` — derselben, die der
 * Server als `BERLIN` führt (`apps/api/app/shared/clock.py:15`) und gegen
 * die er `received_on` prüft (Spec 04b, „Zeit, Idempotenz, Nebenläufigkeit").
 *
 * **Die einzige Stelle im Web-Client, die den heutigen Tag ableitet** —
 * anders als `new Date()` mit der lokalen Zeitzone des Geräts, die östlich
 * von Berlin bereits am Vortag im nächsten Kalendertag steht (Befund
 * W5/B3, `docs/work/004b-zahlungen-b488a7/reports/11-gatekeeper.md`:
 * gemessen mit `Asia/Tokyo`, ab 17:00 Berliner Zeit liegt jede unveränderte
 * Vorbelegung einen Tag zu weit in der Zukunft und wird vom Server mit 422
 * abgewiesen). `en-CA` liefert zuverlässig `JJJJ-MM-TT`, geprüft mit
 * expliziten Feldoptionen statt der impliziten Kurzform.
 */
export function todayInBerlin(): string {
  return BERLIN_DAY_FORMAT.format(new Date())
}

const BERLIN_DISPLAY_FORMAT = new Intl.DateTimeFormat('de-DE', {
  timeZone: 'Europe/Berlin',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
})

/**
 * Einen Zeitpunkt (`datetime`, keine reine Datums-Zeichenkette wie `due_on`)
 * als Berliner Kalendertag anzeigen — `formatDate` oben liest die ersten
 * zehn Zeichen wörtlich; bei einer reinen Datumszeichenkette sind das genau
 * die Kalenderfelder, bei einem `datetime` wie `invited_at` oder
 * `expires_at` stehen dort UTC-Feldern, die nahe der Tagesgrenze den
 * falschen Tag zeigen (Befund A10, `docs/work/005a-…/reports/
 * 14-design-reviewer.md`: `invited_at` erschien als UTC-Tag, obwohl das
 * Projekt den Tag sonst als Berliner Tag führt, `todayInBerlin` hier und
 * `clock.py::BERLIN` auf dem Server).
 */
export function formatInstantInBerlin(iso: string): string {
  return BERLIN_DISPLAY_FORMAT.format(new Date(iso))
}

const BERLIN_OFFSET_FORMAT = new Intl.DateTimeFormat('en-US', {
  timeZone: 'Europe/Berlin',
  timeZoneName: 'shortOffset',
})

/**
 * Der UTC-Versatz von Europe/Berlin in Minuten, gemessen am Mittag (UTC) des
 * übergebenen Kalendertags. Der Wechsel zwischen MEZ und MESZ fällt immer in
 * die frühen Morgenstunden (01–03 Uhr UTC, letzter Sonntag im März/Oktober)
 * — der Mittag desselben Tages liegt damit sicher schon auf der neuen Seite
 * und trägt den Versatz, der für den ganzen restlichen Tag gilt,
 * `berlinEndOfDayIso` (unten) eingeschlossen.
 */
function berlinOffsetMinutesAtNoon(year: number, month: number, day: number): number {
  const noonUtc = new Date(Date.UTC(year, month - 1, day, 12, 0, 0))
  const offsetPart = BERLIN_OFFSET_FORMAT.formatToParts(noonUtc).find((part) => part.type === 'timeZoneName')
  const match = offsetPart === undefined ? null : /GMT([+-]\d+)/.exec(offsetPart.value)
  const hours = match === null ? 1 : Number(match[1])
  return hours * 60
}

/**
 * `"2026-08-22"` → das Tagesende **in Europe/Berlin**, als UTC-Zeitpunkt:
 * `"2026-08-22T21:59:59Z"` im Sommer (MESZ, UTC+2), `"…T22:59:59Z"` im
 * Winter (MEZ, UTC+1).
 *
 * `docs/FACHREGELN.md` Regel 9: Ein Datumsfeld dieser Anwendung meint das
 * Ende des gewählten Tages in Berliner Ortszeit, einschließlich — nicht das
 * UTC-Tagesende. Ersetzt `features/access/format.ts::dateInputToEndOfDayIso`
 * (dort UTC, Befund K3/W2: iOS und Web legten denselben eingegebenen Tag
 * bis zu 24 Stunden verschieden aus).
 */
export function berlinEndOfDayIso(dateInput: string): string {
  const year = Number(dateInput.slice(0, 4))
  const month = Number(dateInput.slice(5, 7))
  const day = Number(dateInput.slice(8, 10))
  const offsetMinutes = berlinOffsetMinutesAtNoon(year, month, day)
  const endOfDayUtcMillis = Date.UTC(year, month - 1, day, 23, 59, 59) - offsetMinutes * 60_000
  return new Date(endOfDayUtcMillis).toISOString().replace(/\.\d{3}Z$/, 'Z')
}
